/**
 * Rock TCS — field metadata, options, and defaults (data only, no JSX).
 * Domain values trace to qa/moftaki-lab-rock-test-requirements-2026-06-10/specs/TCS-CALC-SPEC.md.
 */
import type { RockTCSGeneral, RockTCSConformityReading, RockTCSUltrasonicReading } from '../../../types/rockTcs';

export const CORE_SIZE_OPTIONS = ['HQ', 'NQ'] as const;

/** 11-point flatness positions (mm) by core size. */
export const CONFORMITY_POSITIONS_MM: Record<'HQ' | 'NQ', number[]> = {
  HQ: [0, 6, 12, 18, 24, 30, 36, 42, 48, 54, 60],
  NQ: [0, 4.5, 9, 13.5, 18, 22.5, 27, 31.5, 36, 40.5, 45],
};

/**
 * BUG-2026-0112 — conformity dial readings are ENTERED and DISPLAYED in
 * micrometres (µm), the unit the lab's dial gauges read, but stored and
 * computed in millimetres (`valueMm`, paired with `positionMm`) so the
 * verified flatness/perpendicularity/parallelism math (±0.025 mm, ±0.25°…)
 * stays untouched. These helpers are the only µm↔mm boundary: the cell shows
 * `mmToMicron(valueMm)` and commits `micronToMm(typed)`. Rounded to 0.1 µm
 * (the `Decimal(10,4)` mm resolution) so the ×1000 round-trip introduces no
 * floating-point drift into the input draft.
 */
export const MICRON_PER_MM = 1000;

export function mmToMicron(mm: number | null | undefined): number | null {
  if (mm === null || mm === undefined || !Number.isFinite(mm)) return null;
  return Math.round(mm * MICRON_PER_MM * 10) / 10;
}

export function micronToMm(micron: number | null | undefined): number | null {
  if (micron === null || micron === undefined || !Number.isFinite(micron)) return null;
  return micron / MICRON_PER_MM;
}

export const SPECIMEN_CONDITION_OPTIONS = ['as received', 'oven dried', 'air dried', 'saturated'] as const;

export const FAILURE_MODE_OPTIONS = [
  'Shear',
  'Multiple Shear',
  'Axial Cleavage',
  'Shear & Axial Cleavage',
  'Multiple Shear & Axial Cleavage',
  'Fragmented',
] as const;

export const FAILURE_PLANE_OPTIONS = ['bedding', 'foliation', 'intrusion', 'vein', 'microdefect', 'rock matrix'] as const;

/** Failure modes that use a secondary plane in the description template. */
export const FAILURE_MODES_WITH_SECONDARY = new Set<string>([
  'Multiple Shear',
  'Shear & Axial Cleavage',
  'Multiple Shear & Axial Cleavage',
]);

export const RATE_OF_AXIAL_STRAIN_DEFAULT = 0.06;
export const ULTRASONIC_TOLERANCE_DEFAULT = 0.35;
export const CHAIN_LENGTH_DEFAULT_MM: Record<'HQ' | 'NQ', number | null> = { HQ: 181.5, NQ: null };

/** ASTM shape-tolerance reference values (popup table; fixed constants). */
export const ASTM_TOLERANCE_REFERENCE: { check: string; requirement: string }[] = [
  { check: 'End flatness', requirement: '± 0.025 mm from the best-fit line over each diameter' },
  { check: 'End perpendicularity', requirement: '≤ 0.25° best-fit slope relative to the specimen axis' },
  { check: 'End parallelism', requirement: '≤ 0.13° between the two end best-fit lines (per diameter line)' },
  { check: 'Side straightness', requirement: '≤ 0.5 mm over the full length' },
  { check: 'L/D ratio', requirement: '2.0 – 2.5 (ASTM D7012)' },
];

export const EMPTY_ROCK_TCS_GENERAL: RockTCSGeneral = {
  coreSize: 'HQ',
  rockDescription: null,
  prepDescription: null,
  specimenCondition: 'as received',
  preparedBy: null,
  diameter1: null, diameter2: null, diameter3: null, diameter4: null, diameter5: null, diameter6: null,
  length1: null, length2: null, length3: null, length4: null,
  massG: null,
  leebHardnessEnds: null,
  leebHardnessEdge: null,
  sideStraightnessMm: null,
  conformityFailReason: null,
  ultrasonicCheckTolerance: ULTRASONIC_TOLERANCE_DEFAULT,
  gaugeLength1: null, gaugeLength2: null, gaugeLength3: null,
  chainLengthMm: CHAIN_LENGTH_DEFAULT_MM.HQ,
  confiningStressMPa: null,
  rateOfAxialStrainPctMin: RATE_OF_AXIAL_STRAIN_DEFAULT,
  machineKey: null,
  testDate: null,
  testedBy: null,
  overrideMaxLoadKN: null,
  overrideTCSMPa: null,
  overrideESecantGPa: null,
  overrideETangentGPa: null,
  overridePoissonSecant: null,
  overridePoissonTangent: null,
  overrideDurationS: null,
  altPercentOfTCS: null,
  failureMode: null,
  primaryPlaneType: null,
  primaryPlaneAngleDeg: null,
  secondaryPlaneType: null,
  secondaryPlaneAngleDeg: null,
  failureDescriptionOverride: null,
  remarksText: null,
  additionalObservations: null,
  axisXMaxUE: null, axisXMinUE: null, axisXIntUE: null,
  axisYMaxMPa: null, axisYMinMPa: null, axisYIntMPa: null,
  plotCutoffIndex: null,
};

/** Build the full 44-row conformity entry set for a core size (values null). */
export function emptyConformityReadings(coreSize: 'HQ' | 'NQ'): RockTCSConformityReading[] {
  const rows: RockTCSConformityReading[] = [];
  for (const endNumber of [1, 2]) {
    for (const diameterLine of [1, 2]) {
      CONFORMITY_POSITIONS_MM[coreSize].forEach((positionMm, i) => {
        rows.push({ endNumber, diameterLine, positionMm, valueMm: null, sortOrder: i });
      });
    }
  }
  return rows;
}

export function emptyUltrasonicReadings(): RockTCSUltrasonicReading[] {
  return [1, 2, 3, 4].map((readingNumber) => ({ readingNumber, velocityMS: null, timeUS: null }));
}

/** Reported-value rows for the Peak Test Results panel (auto + override pairs). */
export const PEAK_RESULT_ROWS: {
  label: string;
  unit: string;
  computedField: keyof RockTCSGeneral;
  overrideField?: keyof RockTCSGeneral;
  decimals: number;
  /** BUG-2026-0137 §5 — also display the value as decimal minutes (value/60). */
  showMinutes?: boolean;
}[] = [
  { label: 'Maximum Load (Pmax)', unit: 'kN', computedField: 'computedMaxLoadKN', overrideField: 'overrideMaxLoadKN', decimals: 1 },
  { label: 'Axial Stress at Failure (TCS)', unit: 'MPa', computedField: 'computedTCSMPa', overrideField: 'overrideTCSMPa', decimals: 1 },
  { label: "Young's Modulus — Secant", unit: 'GPa', computedField: 'computedESecantGPa', overrideField: 'overrideESecantGPa', decimals: 1 },
  { label: "Young's Modulus — Tangent", unit: 'GPa', computedField: 'computedETangentGPa', overrideField: 'overrideETangentGPa', decimals: 1 },
  { label: "Poisson's Ratio — Secant", unit: '', computedField: 'computedPoissonSecant', overrideField: 'overridePoissonSecant', decimals: 3 },
  { label: "Poisson's Ratio — Tangent", unit: '', computedField: 'computedPoissonTangent', overrideField: 'overridePoissonTangent', decimals: 3 },
  { label: 'Axial Strain at Failure', unit: 'µε', computedField: 'computedAxialStrainAtFailureUE', decimals: 0 },
  { label: 'Radial Strain at Failure', unit: 'µε', computedField: 'computedRadialStrainAtFailureUE', decimals: 0 },
  { label: 'Volumetric Strain at Failure', unit: 'µε', computedField: 'computedVolStrainAtFailureUE', decimals: 0 },
  { label: 'Test Duration', unit: 's', computedField: 'computedDurationS', overrideField: 'overrideDurationS', decimals: 0, showMinutes: true },
];

/** Combined data table column groups (column config as data, not JSX). */
export interface TcsTableColumn {
  field: string;
  /** Formula Registry key (equals `field`) — passed to `formulaFor()` for the header tooltip. */
  columnKey: string;
  label: string;
  unit: string;
  decimals: number;
  group: 'Imported' | 'Stress' | 'Strains (µε)' | 'Moduli & Ratios';
}

export const TCS_TABLE_COLUMNS: TcsTableColumn[] = [
  { field: 'timeS', columnKey: 'timeS', label: 'Time', unit: 's', decimals: 3, group: 'Imported' },
  { field: 'frameLoadKN', columnKey: 'frameLoadKN', label: 'Frame Load', unit: 'kN', decimals: 3, group: 'Imported' },
  { field: 'axialDisp1Mm', columnKey: 'axialDisp1Mm', label: 'Axial Disp 1', unit: 'mm', decimals: 4, group: 'Imported' },
  { field: 'axialDisp2Mm', columnKey: 'axialDisp2Mm', label: 'Axial Disp 2', unit: 'mm', decimals: 4, group: 'Imported' },
  { field: 'radialStrainPct', columnKey: 'radialStrainPct', label: 'Radial Strain', unit: '%', decimals: 4, group: 'Imported' },
  { field: 'computedAxialStressMPa', columnKey: 'computedAxialStressMPa', label: 'Axial Stress', unit: 'MPa', decimals: 2, group: 'Stress' },
  { field: 'computedAxialStrain1UE', columnKey: 'computedAxialStrain1UE', label: 'Axial Strain 1', unit: 'µε', decimals: 1, group: 'Strains (µε)' },
  { field: 'computedAxialStrain2UE', columnKey: 'computedAxialStrain2UE', label: 'Axial Strain 2', unit: 'µε', decimals: 1, group: 'Strains (µε)' },
  { field: 'computedAvgAxialStrainUE', columnKey: 'computedAvgAxialStrainUE', label: 'Avg Axial', unit: 'µε', decimals: 1, group: 'Strains (µε)' },
  { field: 'computedRadialStrainUE', columnKey: 'computedRadialStrainUE', label: 'Radial', unit: 'µε', decimals: 1, group: 'Strains (µε)' },
  { field: 'computedVolumetricStrainUE', columnKey: 'computedVolumetricStrainUE', label: 'Volumetric', unit: 'µε', decimals: 1, group: 'Strains (µε)' },
  { field: 'computedESecantGPa', columnKey: 'computedESecantGPa', label: 'E Secant', unit: 'GPa', decimals: 2, group: 'Moduli & Ratios' },
  { field: 'computedETangentGPa', columnKey: 'computedETangentGPa', label: 'E Tangent', unit: 'GPa', decimals: 2, group: 'Moduli & Ratios' },
  { field: 'computedPoissonSecant', columnKey: 'computedPoissonSecant', label: 'ν Secant', unit: '', decimals: 4, group: 'Moduli & Ratios' },
  { field: 'computedPoissonTangent', columnKey: 'computedPoissonTangent', label: 'ν Tangent', unit: '', decimals: 4, group: 'Moduli & Ratios' },
];
